import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import debounce from "lodash/debounce";
import IntakeProgressBar from "pages/intake/components/IntakeProgressBar";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import {
  useBulkRREImportForClientMutation,
  useGetClientsQuery,
} from "services/client-api";
import type { ClientType } from "types/DataTypes";
import { useToken } from "utils/context";
import { logToSentry } from "utils/sentry";
import "./BulkImportRREModal.scss";

import KaboodleModal from "./KaboodleModal";

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1GB in bytes

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  client_id: OptionType;
};

const BulkImportRREModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { accessToken } = useToken();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      client_id: null,
    },
  });

  const [bulkImportRRE, { isLoading }] = useBulkRREImportForClientMutation();

  const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string | null>(null);

  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !clientSearchTerm },
  );

  useEffect(() => {
    if (clients) {
      setClientOptions(
        clients.results?.map((client: ClientType) => ({
          label: client.name,
          value: client.id,
        })) ?? [],
      );
    }
  }, [clients]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      if (!selectedFile.name.endsWith(".zip")) {
        const errorMsg = "Please upload a .zip file";
        KaboodleToaster.error(errorMsg);
        logToSentry("BulkImportRREModal: Invalid file type", "warn", {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
        });
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        const fileSizeGB = (selectedFile.size / (1024 * 1024 * 1024)).toFixed(
          2,
        );
        const errorMsg = `File size (${fileSizeGB}GB) exceeds the 1GB limit`;
        KaboodleToaster.error(errorMsg);
        logToSentry("BulkImportRREModal: File size limit exceeded", "warn", {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileSizeGB,
        });
        return;
      }

      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
    multiple: false,
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev; // Cap at 90% until complete
          // Add random increment between 1 and 10
          const increment = Math.floor(Math.random() * 10) + 1;
          return Math.min(prev + increment, 90);
        });
      }, 500);
    } else {
      setProgress(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  const handleClose = useCallback(() => {
    reset();
    setFile(null);
    setClientSearchTerm(null);
    setProgress(0);
    onClose();
  }, [reset, onClose]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!data.client_id) {
        KaboodleToaster.error("Please select a client");
        return;
      }

      if (!file) {
        KaboodleToaster.error("Please select a file to upload");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        await bulkImportRRE({
          accessToken,
          id: data.client_id.value,
          file: formData,
        }).unwrap();

        KaboodleToaster.success(
          "Bulk import started successfully. RREs will be available in the next 10 minutes.",
          { duration: 5000 },
        );
        logToSentry("BulkImportRREModal: Import started successfully", "info", {
          clientId: data.client_id.value,
          fileName: file.name,
          fileSize: file.size,
        });
        handleClose();
      } catch (err: unknown) {
        console.error("Failed to import RREs:", err);

        const errorData = (err as { data?: { error?: string } })?.data;
        const backendError = errorData?.error;

        const errorMessage =
          backendError ||
          "Failed to import RREs. The file format may be incorrect. Please ensure your ZIP file contains valid RRE PDF documents.";

        KaboodleToaster.error(errorMessage, { duration: 7000 });

        logToSentry("BulkImportRREModal: Import failed", "error", {
          clientId: data.client_id.value,
          fileName: file.name,
          fileSize: file.size,
          error: backendError || "Unknown error",
          errorDetails: err,
        });
      }
    },
    [bulkImportRRE, accessToken, file, handleClose],
  );

  const footerContent = useMemo(() => {
    return (
      <Button
        onClick={handleSubmit(onSubmit, (errors) => {
          if (errors.client_id) {
            KaboodleToaster.error("Please select a client");
          }
        })}
        disabled={!file || isLoading}
      >
        {isLoading ? "Processing documents..." : "Upload"}
      </Button>
    );
  }, [handleSubmit, onSubmit, file, isLoading]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <div className="bulk-import-rre-modal">
        <Card heading="Bulk Import RREs">
          <p className="mrg-bottom">
            Upload a ZIP file containing RRE documents to import.
          </p>

          <Controller
            control={control}
            name="client_id"
            rules={{ required: "Client is required" }}
            render={({ field: { name, onChange } }) => (
              <Select
                name={name}
                placeholder="Search Client..."
                label="Client"
                options={clientOptions}
                errors={errors}
                noOptionsMessage={({ inputValue }) =>
                  !inputValue
                    ? "Begin typing to search for clients..."
                    : isClientsFetching
                      ? "Searching..."
                      : "No clients found."
                }
                onInputChange={(inputValue: string) =>
                  debouncedSetClientSearchTerm(inputValue)
                }
                onChange={onChange}
              />
            )}
          />

          {!isLoading && (
            <div
              {...getRootProps()}
              className={`dropzone ${isDragActive ? "active" : ""}`}
            >
              <input {...getInputProps()} />
              {file ? (
                <p>
                  <b>Selected file:</b> {file.name}
                </p>
              ) : (
                <p>
                  <b>Drag & Drop</b> or <b>Click</b> to Select ZIP File
                </p>
              )}
            </div>
          )}

          {isLoading && (
            <div className="upload-progress">
              <p className="mrg-bottom">Processing documents...</p>
              <IntakeProgressBar
                progress={progress}
                height="10px"
                fillColor="#3c1283" // $allan-koba
                className="w-100"
              />
            </div>
          )}
        </Card>
      </div>
    </KaboodleModal>
  );
};

export default BulkImportRREModal;
